<!-- src/components/stock/StockOrderApprovals.vue -->
<template>
  <section class="order-approvals">
    <div class="core-section-header approval-heading">
      <div class="core-section-header-main">
        <h5>Order Approvals</h5>

        <p class="grey-text text-darken-1">
          Select an order to review quantities, make amendments and approve it for
          delivery.
        </p>
      </div>

      <span class="core-badge core-badge--danger core-badge--rect">
        {{ orders.length }}
      </span>
    </div>

    <div v-if="loading" class="progress">
      <div class="indeterminate"></div>
    </div>

    <div v-else-if="error" class="card-panel red lighten-4 red-text text-darken-4">
      {{ error }}
    </div>

    <div
      v-else-if="!orders.length"
      class="core-empty-state core-empty-state--inline core-empty-state--success"
    >
      <i class="material-icons"> task_alt </i>

      <strong class="core-empty-state-title"> No orders are awaiting approval. </strong>
    </div>

    <div v-else class="approval-table-list">
      <article
        v-for="order in orders"
        :key="orderKey(order)"
        class="core-list-card approval-card"
        :class="{
          'core-list-card--open': isOrderOpen(order),
        }"
      >
        <!-- =================================================
             COLLAPSIBLE HEADER
             ================================================= -->
        <button
          type="button"
          class="core-disclosure-header"
          :aria-expanded="isOrderOpen(order)"
          :aria-controls="`approval-order-${safeId(orderKey(order))}`"
          @click="toggleOrder(order)"
        >
          <div class="order-header-fields">
            <span class="order-header-field">
              <span class="field-label"> Order Number: </span>

              <strong class="field-value">
                {{ order.orderReference || order.id }}
              </strong>
            </span>

            <span class="header-divider"> | </span>

            <span class="order-header-field">
              <span class="field-label"> Supplier: </span>

              <strong class="field-value">
                {{ order.supplier || order.items?.[0]?.supplier || "—" }}
              </strong>
            </span>

            <span class="header-divider"> | </span>

            <span class="order-header-field">
              <span class="field-label"> Venue: </span>

              <strong class="field-value">
                {{
                  order.orderDetails?.venueName || order.venueName || order.venueId || "—"
                }}
              </strong>
            </span>

            <span class="header-divider"> | </span>

            <span class="order-header-field total-header-field">
              <span class="field-label"> Total Value: </span>

              <strong class="field-value">
                {{ money(approvedTotal(order)) }}
              </strong>
            </span>
          </div>

          <div class="core-disclosure-header-actions order-header-status">
            <span
              v-if="isOverspend(order)"
              class="core-badge core-badge--danger core-badge--rect"
              title="This order exceeded the weekly liquor ordering budget when submitted"
            >
              Overspend
            </span>

            <span class="core-badge core-badge--warning core-badge--rect">
              Awaiting Approval
            </span>

            <i
              class="material-icons core-disclosure-chevron"
              :class="{
                open: isOrderOpen(order),
              }"
              aria-hidden="true"
            >
              expand_more
            </i>
          </div>
        </button>

        <!-- =================================================
             COLLAPSIBLE BODY
             ================================================= -->
        <div
          v-show="isOrderOpen(order)"
          :id="`approval-order-${safeId(orderKey(order))}`"
          class="core-disclosure-body approval-card-body"
        >
          <div class="core-meta-row">
            <span>
              <strong> Supplier: </strong>

              {{ order.supplier || "—" }}
            </span>

            <span>
              <strong> Submitted by: </strong>

              {{ order.createdByName || order.createdByEmail || "—" }}
            </span>

            <span>
              <strong> Submitted: </strong>

              {{ formatDate(order.submittedAt || order.createdAt) }}
            </span>
          </div>

          <!-- =================================================
               OVERSPEND INFO
               ================================================= -->
          <section v-if="isOverspend(order)" class="overspend-review">
            <div class="overspend-review-heading">
              <div>
                <span class="overspend-review-title">
                  <i class="material-icons" aria-hidden="true"> warning </i>

                  Weekly Liquor Budget Overspend
                </span>

                <p>
                  This information was captured when the order was submitted and forms
                  part of this existing approval request.
                </p>
              </div>

              <strong class="overspend-amount">
                {{ money(order.budget?.overspendAmount || 0) }}
                over
              </strong>
            </div>

            <div class="core-review-grid overspend-budget-grid">
              <div class="core-review-box">
                <span> Previous Week Liquor Sales </span>

                <strong>
                  {{ money(order.budget?.previousWeekLiquorSales || 0) }}
                </strong>
              </div>

              <div class="core-review-box">
                <span> Weekly Budget (28%) </span>

                <strong>
                  {{ money(order.budget?.weeklyBudget || 0) }}
                </strong>
              </div>

              <div class="core-review-box">
                <span> Orders Before This Order </span>

                <strong>
                  {{ money(order.budget?.ordersBeforeThisOrder || 0) }}
                </strong>
              </div>

              <div class="core-review-box">
                <span> This Submitted Order </span>

                <strong>
                  {{ money(order.budget?.thisOrder || order.totals?.orderTotal || 0) }}
                </strong>
              </div>

              <div class="core-review-box">
                <span> Projected Weekly Orders </span>

                <strong>
                  {{ money(order.budget?.projectedOrders || 0) }}
                </strong>
              </div>

              <div class="core-review-box overspend-box">
                <span> Overspend </span>

                <strong>
                  {{ money(order.budget?.overspendAmount || 0) }}
                </strong>
              </div>
            </div>

            <div class="overspend-reason">
              <span> Reason for overspend </span>

              <p>
                {{ order.overspend?.reason || "No reason supplied." }}
              </p>
            </div>
          </section>

          <!-- =================================================
               ORDER LINES
               ================================================= -->
          <div class="table-wrap">
            <table class="striped core-table approval-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Supplier</th>

                  <th class="right-align">Unit Cost</th>

                  <th class="center-align">Qty</th>

                  <th class="right-align">Line Total</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="item in order.items || []"
                  :key="`${orderKey(order)}-${item.id}`"
                >
                  <td class="core-product-cell">
                    {{ item.product }}
                  </td>

                  <td>
                    {{ item.supplier || order.supplier || "—" }}
                  </td>

                  <td class="right-align core-money-cell">
                    {{ money(item.unitPrice) }}
                  </td>

                  <td class="center-align">
                    <input
                      v-model.number="drafts[orderKey(order)][item.id]"
                      type="number"
                      min="0"
                      step="1"
                      inputmode="numeric"
                      class="core-quantity-input"
                      :aria-label="`Approved quantity for ${item.product}`"
                      :disabled="busyOrderKey === orderKey(order)"
                    />
                  </td>

                  <td class="right-align core-money-cell">
                    {{ money(lineTotal(order, item)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- =================================================
               APPROVAL FOOTER
               ================================================= -->
          <div class="approval-footer">
            <div class="core-inline approval-summary">
              <span>
                {{ approvedLineCount(order) }}
                lines
              </span>

              <span>
                {{ approvedQuantity(order) }}
                units
              </span>

              <strong>
                {{ money(approvedTotal(order)) }}
              </strong>
            </div>

            <div class="core-actions core-actions--right approval-actions">
              <button
                v-if="isAmended(order)"
                class="btn-flat waves-effect"
                type="button"
                :disabled="busyOrderKey === orderKey(order)"
                @click="resetOrder(order)"
              >
                Reset Changes
              </button>

              <button
                class="btn-flat red-text text-darken-2 waves-effect"
                type="button"
                :disabled="busyOrderKey === orderKey(order)"
                @click="openDecisionModal(order, 'Rejected')"
              >
                <i class="material-icons left"> cancel </i>

                Reject Order
              </button>

              <button
                class="btn amber darken-3 waves-effect waves-light"
                type="button"
                :disabled="busyOrderKey === orderKey(order)"
                @click="openDecisionModal(order, 'Review Required')"
              >
                <i class="material-icons left"> edit_note </i>

                Return for Amendment
              </button>

              <button
                class="btn teal darken-2 waves-effect waves-light"
                type="button"
                :disabled="
                  busyOrderKey === orderKey(order) || approvedLineCount(order) === 0
                "
                @click="approveOrder(order)"
              >
                <i class="material-icons left"> check_circle </i>

                {{
                  busyOrderKey === orderKey(order)
                    ? "Approving..."
                    : isAmended(order)
                    ? "Approve Amended Order"
                    : "Approve Order"
                }}
              </button>
            </div>
          </div>
        </div>
      </article>
    </div>

    <!-- =====================================================
         DECISION MODAL
         ===================================================== -->
    <div
      v-if="decisionModal.open"
      class="core-modal-backdrop"
      @click.self="closeDecisionModal"
    >
      <section
        class="core-modal core-modal--medium core-modal--padded"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-decision-modal-title"
      >
        <div class="core-modal-header">
          <div class="core-modal-header-main">
            <h5 id="order-decision-modal-title" class="core-modal-title">
              {{
                decisionModal.decision === "Rejected"
                  ? "Reject Order"
                  : "Return Order for Amendment"
              }}
            </h5>

            <p class="core-modal-subtitle">
              {{ decisionModal.order?.orderReference || decisionModal.order?.id }}
            </p>
          </div>

          <button
            type="button"
            class="core-modal-close"
            aria-label="Close order decision form"
            :disabled="Boolean(busyOrderKey)"
            @click="closeDecisionModal"
          >
            <i class="material-icons"> close </i>
          </button>
        </div>

        <div class="core-modal-body">
          <div
            v-if="decisionModal.decision === 'Review Required'"
            class="core-callout core-callout--warning"
          >
            The venue will see your comments and the quantities currently shown above. It
            can edit the order and resubmit it for approval.
          </div>

          <div v-else class="core-callout core-callout--danger">
            The venue will see your rejection reason. This order will not create a
            delivery note.
          </div>

          <div class="input-field decision-comments-field">
            <textarea
              id="order-decision-comments"
              v-model="decisionModal.comments"
              class="materialize-textarea"
              maxlength="2000"
              :disabled="Boolean(busyOrderKey)"
              autofocus
            ></textarea>

            <label for="order-decision-comments" class="active">
              {{
                decisionModal.decision === "Rejected"
                  ? "Reason for rejection"
                  : "Amendments required"
              }}
            </label>
          </div>

          <p
            class="decision-requirement"
            :class="{
              valid: decisionCommentsValid,
            }"
          >
            Minimum 3 characters required.
            {{ decisionModal.comments.trim().length }}/3
          </p>
        </div>

        <div class="core-modal-actions">
          <button
            type="button"
            class="btn-flat"
            :disabled="Boolean(busyOrderKey)"
            @click="closeDecisionModal"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn waves-effect waves-light"
            :class="
              decisionModal.decision === 'Rejected' ? 'red darken-2' : 'amber darken-3'
            "
            :disabled="Boolean(busyOrderKey) || !decisionCommentsValid"
            @click="submitDecision"
          >
            <i class="material-icons left">
              {{ decisionModal.decision === "Rejected" ? "cancel" : "edit_note" }}
            </i>

            {{ decisionSubmitLabel }}
          </button>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { collectionGroup, onSnapshot } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import { db } from "@/firebase";

const functions = getFunctions(undefined, "us-central1");

const approveLiquorOrderCallable = httpsCallable(functions, "approveLiquorOrder");

const submitLiquorOrderDecisionCallable = httpsCallable(
  functions,
  "submitLiquorOrderDecision"
);

export default {
  name: "StockOrderApprovals",

  emits: ["pending-count"],

  data() {
    return {
      orders: [],

      drafts: {},

      openOrders: {},

      loading: true,

      error: null,

      busyOrderKey: "",

      decisionModal: {
        open: false,

        order: null,

        decision: "",

        comments: "",
      },

      unsubscribe: null,
    };
  },

  computed: {
    decisionCommentsValid() {
      return this.decisionModal.comments.trim().length >= 3;
    },

    decisionSubmitLabel() {
      if (this.busyOrderKey) {
        return "Submitting...";
      }

      return this.decisionModal.decision === "Rejected"
        ? "Confirm Rejection"
        : "Request Amendment";
    },
  },

  mounted() {
    this.subscribe();
  },

  beforeUnmount() {
    this.unsubscribe?.();

    this.unsubscribe = null;
  },

  methods: {
    orderKey(order) {
      return `${order.venueId}-` + `${order.id}`;
    },

    safeId(value) {
      return String(value || "").replace(/[^a-zA-Z0-9_-]/g, "-");
    },

    isOrderOpen(order) {
      return Boolean(this.openOrders[this.orderKey(order)]);
    },

    toggleOrder(order) {
      const key = this.orderKey(order);

      this.openOrders[key] = !this.openOrders[key];
    },

    isOverspend(order) {
      return Boolean(
        order?.overspend?.required === true ||
          order?.budget?.isOverspend === true ||
          this.number(order?.budget?.overspendAmount) > 0
      );
    },

    number(value) {
      const number = Number(value);

      return Number.isFinite(number) ? number : 0;
    },

    cleanQuantity(value) {
      return Math.max(0, Math.floor(this.number(value)));
    },

    timestampMillis(value) {
      if (typeof value?.toMillis === "function") {
        return value.toMillis();
      }

      if (value?.seconds) {
        return Number(value.seconds) * 1000;
      }

      const parsed = new Date(value || 0).getTime();

      return Number.isFinite(parsed) ? parsed : 0;
    },

    subscribe() {
      this.unsubscribe?.();

      this.loading = true;

      this.error = null;

      this.unsubscribe = onSnapshot(
        collectionGroup(db, "liquor-orders"),

        (snapshot) => {
          const orders = snapshot.docs
            .map((orderDocument) => ({
              id: orderDocument.id,

              venueId: orderDocument.ref.parent.parent?.id || "",

              ...orderDocument.data(),
            }))
            .filter((order) => {
              return (
                order.status === "pending_approval" && order.approvalStatus === "pending"
              );
            })
            .sort((a, b) => {
              return (
                this.timestampMillis(b.submittedAt || b.createdAt) -
                this.timestampMillis(a.submittedAt || a.createdAt)
              );
            });

          const liveKeys = new Set(orders.map((order) => this.orderKey(order)));

          for (const existingKey of Object.keys(this.drafts)) {
            if (!liveKeys.has(existingKey)) {
              delete this.drafts[existingKey];
            }
          }

          for (const existingKey of Object.keys(this.openOrders)) {
            if (!liveKeys.has(existingKey)) {
              delete this.openOrders[existingKey];
            }
          }

          for (const order of orders) {
            const key = this.orderKey(order);

            if (!this.drafts[key]) {
              this.setDraft(order);
            }

            if (!Object.prototype.hasOwnProperty.call(this.openOrders, key)) {
              this.openOrders[key] = false;
            }
          }

          this.orders = orders;

          this.$emit("pending-count", orders.length);

          this.loading = false;
        },

        (error) => {
          console.error("[StockOrderApprovals] Load failed:", error);

          this.error = error?.message || "Unable to load pending orders.";

          this.loading = false;
        }
      );
    },

    setDraft(order) {
      const items = Array.isArray(order.items) ? order.items : [];

      this.drafts[this.orderKey(order)] = Object.fromEntries(
        items.map((item) => [item.id, this.cleanQuantity(item.quantity)])
      );
    },

    resetOrder(order) {
      this.setDraft(order);
    },

    openDecisionModal(order, decision) {
      if (this.busyOrderKey) {
        return;
      }

      this.decisionModal = {
        open: true,

        order,

        decision,

        comments: "",
      };

      this.$nextTick(() => {
        window.M?.updateTextFields?.();
      });
    },

    closeDecisionModal() {
      if (this.busyOrderKey) {
        return;
      }

      this.decisionModal = {
        open: false,

        order: null,

        decision: "",

        comments: "",
      };
    },

    async submitDecision() {
      const order = this.decisionModal.order;

      const decision = this.decisionModal.decision;

      const comments = this.decisionModal.comments.trim();

      if (!order || !this.decisionCommentsValid || this.busyOrderKey) {
        return;
      }

      const key = this.orderKey(order);

      this.busyOrderKey = key;

      this.error = null;

      try {
        await submitLiquorOrderDecisionCallable({
          venueId: order.venueId,

          orderId: order.id,

          decision,

          comments,

          items: decision === "Review Required" ? this.amendedItems(order) : undefined,
        });

        window.M?.toast?.({
          html:
            decision === "Rejected"
              ? `${order.orderReference || "Order"} rejected`
              : `${order.orderReference || "Order"} returned for amendment`,

          classes: decision === "Rejected" ? "red darken-2" : "amber darken-3",
        });

        this.decisionModal = {
          open: false,

          order: null,

          decision: "",

          comments: "",
        };
      } catch (error) {
        console.error("[StockOrderApprovals] Decision failed:", error);

        this.error =
          error?.details?.message ||
          error?.message ||
          "Unable to submit this order decision.";
      } finally {
        this.busyOrderKey = "";
      }
    },

    amendedItems(order) {
      const items = Array.isArray(order.items) ? order.items : [];

      const draft = this.drafts[this.orderKey(order)] || {};

      return items
        .map((item) => ({
          ...item,

          quantity: this.cleanQuantity(draft[item.id]),
        }))
        .filter((item) => item.quantity > 0);
    },

    isAmended(order) {
      const items = Array.isArray(order.items) ? order.items : [];

      const draft = this.drafts[this.orderKey(order)] || {};

      return items.some((item) => {
        const approvedQuantity = this.cleanQuantity(draft[item.id]);

        const originalQuantity = this.cleanQuantity(item.quantity);

        return approvedQuantity !== originalQuantity;
      });
    },

    approvedLineCount(order) {
      return this.amendedItems(order).length;
    },

    approvedQuantity(order) {
      return this.amendedItems(order).reduce((total, item) => total + item.quantity, 0);
    },

    approvedTotal(order) {
      return this.amendedItems(order).reduce((total, item) => {
        return total + this.number(item.unitPrice) * item.quantity;
      }, 0);
    },

    lineTotal(order, item) {
      const draft = this.drafts[this.orderKey(order)] || {};

      const quantity = this.cleanQuantity(draft[item.id]);

      return this.number(item.unitPrice) * quantity;
    },

    async approveOrder(order) {
      const key = this.orderKey(order);

      if (this.busyOrderKey || !this.approvedLineCount(order)) {
        return;
      }

      this.busyOrderKey = key;

      this.error = null;

      try {
        await approveLiquorOrderCallable({
          venueId: order.venueId,

          orderId: order.id,

          items: this.amendedItems(order),
        });

        window.M?.toast?.({
          html: `${order.orderReference || "Order"} approved for delivery`,

          classes: "green darken-2",
        });
      } catch (error) {
        console.error("[StockOrderApprovals] Approval failed:", error);

        this.error =
          error?.details?.message || error?.message || "Unable to approve this order.";
      } finally {
        this.busyOrderKey = "";
      }
    },

    formatDate(value) {
      if (!value) {
        return "—";
      }

      let date = null;

      if (typeof value?.toDate === "function") {
        date = value.toDate();
      } else if (value?.seconds) {
        date = new Date(Number(value.seconds) * 1000);
      } else {
        date = new Date(value);
      }

      if (!date || Number.isNaN(date.getTime())) {
        return "—";
      }

      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },

    money(value) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(this.number(value));
    },
  },
};
</script>

<style scoped>
.order-approvals {
  width: 100%;
}

.approval-heading h5 {
  margin: 0 0 5px;
  font-weight: 700;
}

.approval-heading p {
  margin: 0;
}

.approval-table-list {
  display: grid;
  gap: 10px;
}

.order-header-fields {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
  white-space: nowrap;
  overflow: hidden;
}

.order-header-field {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}

.field-label {
  color: #607d8b;
  font-size: 0.84rem;
  font-weight: 600;
}

.field-value {
  min-width: 0;
  overflow: hidden;
  color: #263238;
  font-size: 0.9rem;
  text-overflow: ellipsis;
}

.header-divider {
  flex: 0 0 auto;
  color: #b0bec5;
}

.total-header-field .field-value {
  color: #00695c;
}

.order-header-status {
  margin-left: 22px;
}

.approval-card-body {
  padding-top: 0;
}

.overspend-review {
  margin: 2px 0 18px;
  padding: 16px;
  border: 1px solid #ef9a9a;
  border-left: 5px solid #c62828;
  border-radius: 6px;
  background: #fff8f8;
}

.overspend-review-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
}

.overspend-review-title {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #b71c1c;
  font-size: 1rem;
  font-weight: 800;
}

.overspend-review-title i {
  font-size: 20px;
}

.overspend-review-heading p {
  margin: 5px 0 0;
  color: #616161;
  font-size: 0.86rem;
}

.overspend-amount {
  flex: 0 0 auto;
  color: #b71c1c;
  font-size: 1.08rem;
}

.overspend-box {
  border-color: #ef9a9a;
  background: #ffebee;
}

.overspend-box strong {
  color: #b71c1c;
}

.overspend-reason {
  margin-top: 12px;
  padding: 11px 13px;
  border-radius: 5px;
  background: #ffffff;
}

.overspend-reason span {
  display: block;
  margin-bottom: 4px;
  color: #b71c1c;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.overspend-reason p {
  margin: 0;
  color: #263238;
  white-space: pre-wrap;
}

.approval-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #eceff1;
}

.approval-summary {
  color: #607d8b;
}

.approval-summary strong {
  color: #00695c;
  font-size: 1.05rem;
}

.decision-comments-field {
  margin-top: 30px;
}

.decision-comments-field textarea {
  min-height: 120px;
}

.decision-requirement {
  margin: -8px 0 0;
  color: #c62828;
  font-size: 0.86rem;
  font-weight: 600;
}

.decision-requirement.valid {
  color: #2e7d32;
}

@media (max-width: 1100px) {
  .order-header-fields {
    flex-wrap: wrap;
    white-space: normal;
  }

  .header-divider {
    display: none;
  }

  .order-header-field {
    margin-right: 10px;
  }
}

@media (max-width: 700px) {
  .approval-footer,
  .overspend-review-heading {
    flex-direction: column;
  }

  .order-header-status {
    width: 100%;
    margin-left: 0;
  }

  .approval-footer {
    align-items: stretch;
  }

  .approval-summary {
    justify-content: space-between;
  }
}
</style>
